import { useState, lazy, Suspense } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { formatDistance, formatDuration, formatDate } from '../../utils/formatters';
import { downloadGPX } from '../../utils/gpx';
import { buildShareURL, copyToClipboard } from '../../utils/share';
import { ProModal } from '../ProModal/ProModal';
import { RouteThumbnail } from '../common/RouteThumbnail';
import './SavedRoutes.css';

const PublishModal = lazy(() =>
  import('../PublishModal/PublishModal').then(m => ({ default: m.PublishModal }))
);

// ── Lucide-style icons ────────────────────────────────────────────────────
const IC = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

function MotorcycleIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <circle cx="5" cy="17" r="3"/>
      <circle cx="19" cy="17" r="3"/>
      <path d="M9 17l2-9h3l3 5.5"/>
      <path d="M14 8l2-3h3"/>
      <path d="M5 14l4-6"/>
      <path d="M17 13.5L19 14"/>
    </svg>
  );
}
function BikeIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <circle cx="18.5" cy="17.5" r="3.5"/>
      <circle cx="5.5" cy="17.5" r="3.5"/>
      <circle cx="15" cy="5" r="1"/>
      <path d="M12 17.5V14l-3-3 4-3 2 3h2"/>
    </svg>
  );
}
function WalkIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <circle cx="13" cy="4" r="1.5"/>
      <path d="m9 20 2-6 3 3 2-8"/>
      <path d="m6 15 2-4 4 1"/>
      <path d="m12 18 2 2h4"/>
    </svg>
  );
}
function DownloadIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}
function ShareIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );
}
function GlobeIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}
function CheckGlobeIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );
}
function TrashIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}
function ChevronIcon({ up, size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...IC}>
      <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/>
    </svg>
  );
}

const PROFILE_META = {
  driving: { Icon: MotorcycleIcon, label: 'Motorcycle', color: '#ff7a1a', bg: '#fff3ea' },
  // Legacy fallbacks for any routes saved before Motoroam went motorcycle-only.
  cycling: { Icon: BikeIcon, label: 'Cycling',  color: '#2563eb', bg: '#eff6ff' },
  foot:    { Icon: WalkIcon, label: 'Walking',  color: '#f59e0b', bg: '#fffbeb' },
};

// Publishing is free while the community is being seeded (cold start). Flip to
// false to put it back behind Pro once there's a healthy library.
const PUBLISH_FREE = true;

export function SavedRoutes() {
  const { state, dispatch, showToast } = useRoute();
  const { user, isSubscribed, signIn } = useAuth();
  const { saved, current } = state;

  const [expanded, setExpanded]       = useState(true);
  const [proOpen, setProOpen]         = useState(false);
  const [publishRoute, setPublishRoute] = useState(null);

  const handleLoad = (route) => {
    dispatch({ type: 'LOAD_ROUTE', payload: { ...route } });
    showToast(`Loaded "${route.name}"`, 'info');
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    dispatch({ type: 'DELETE_ROUTE', payload: id });
    showToast('Route deleted', 'info');
  };

  const handleExport = async (e, route) => {
    e.stopPropagation();
    await downloadGPX(route, { rich: isSubscribed });
    showToast(
      isSubscribed ? 'GPX downloaded — elevation included' : 'GPX downloaded · Pro bakes in elevation',
      'success'
    );
  };

  const handleShare = async (e, route) => {
    e.stopPropagation();
    const url = buildShareURL(route);
    await copyToClipboard(url);
    showToast('Share link copied!', 'success');
  };

  const handlePublish = (e, route) => {
    e.stopPropagation();
    if (route.publishedId) return;
    if (!user) { signIn(); return; }          // need an author on the route
    if (PUBLISH_FREE || isSubscribed) {
      setPublishRoute(route);
    } else {
      setProOpen(true);
    }
  };

  return (
    <div className="saved-routes__section">
      <button className="saved-routes__header" onClick={() => setExpanded(v => !v)}>
        <span className="saved-routes__title">
          Saved Routes
          {saved.length > 0 && <span className="saved-routes__count">{saved.length}</span>}
        </span>
        <ChevronIcon up={expanded} />
      </button>

      {expanded && saved.length === 0 && (
        <p className="saved-routes__empty">No saved routes yet — create one above!</p>
      )}

      {expanded && saved.length > 0 && (
        <ul className="saved-routes__list">
          {saved.map(route => {
            const isActive = current.id === route.id;

            return (
              <li
                key={route.id}
                className={`sr-card${isActive ? ' sr-card--active' : ''}`}
                onClick={() => handleLoad(route)}
              >
                {/* Colored left stripe */}
                <div className="sr-card__stripe" />

                {(route.geometry || route.waypoints?.length >= 2) && (
                  <div className="sr-card__thumb">
                    <RouteThumbnail geometry={route.geometry} waypoints={route.waypoints} />
                  </div>
                )}

                <div className="sr-card__body">
                  {/* Top row: active badge (only when current) */}
                  {isActive && (
                    <div className="sr-card__top">
                      <span className="sr-card__active-badge">Active</span>
                    </div>
                  )}

                  {/* Route name */}
                  <div className="sr-card__name">{route.name}</div>

                  {/* Stats */}
                  <div className="sr-card__stats">
                    {route.distance != null && (
                      <span className="sr-card__stat">
                        <svg width="11" height="11" viewBox="0 0 24 24" {...IC}><path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/></svg>
                        {formatDistance(route.distance)}
                      </span>
                    )}
                    {route.duration != null && (
                      <span className="sr-card__stat">
                        <svg width="11" height="11" viewBox="0 0 24 24" {...IC}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        {formatDuration(route.duration)}
                      </span>
                    )}
                    <span className="sr-card__stat">
                      <svg width="11" height="11" viewBox="0 0 24 24" {...IC}><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                      {route.waypoints.filter(w => !w.isLoopEnd).length} stops
                    </span>
                  </div>

                  {route.savedAt && (
                    <div className="sr-card__date">{formatDate(route.savedAt)}</div>
                  )}

                  {/* Action bar */}
                  <div className="sr-card__actions">
                    <button
                      className="sr-card__btn"
                      onClick={e => handleExport(e, route)}
                      title="Export GPX"
                    >
                      <DownloadIcon /> <span>GPX</span>
                    </button>
                    <button
                      className="sr-card__btn"
                      onClick={e => handleShare(e, route)}
                      title="Copy share link"
                    >
                      <ShareIcon /> <span>Share</span>
                    </button>
                    <button
                      className={`sr-card__btn sr-card__btn--publish${route.publishedId ? ' sr-card__btn--published' : ''}`}
                      onClick={e => handlePublish(e, route)}
                      title={route.publishedId ? 'Published to community' : 'Publish to community'}
                      disabled={!!route.publishedId}
                    >
                      {route.publishedId ? <CheckGlobeIcon /> : <GlobeIcon />}
                      <span>{route.publishedId ? 'Published' : 'Publish'}</span>
                    </button>
                    <button
                      className="sr-card__btn sr-card__btn--danger"
                      onClick={e => handleDelete(e, route.id)}
                      title="Delete route"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {proOpen && <ProModal onClose={() => setProOpen(false)} />}
      {publishRoute && (
        <Suspense fallback={null}>
          <PublishModal
            route={publishRoute}
            onClose={() => setPublishRoute(null)}
            onSuccess={(publishedId) => {
              dispatch({ type: 'MARK_PUBLISHED', payload: { routeId: publishRoute.id, publishedId } });
              showToast('Route published to community!', 'success');
              setPublishRoute(null);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
